import React, { useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { apiErrorMessage, apiErrorStatus, backendClient } from '@/lib/api-client';
import type { Deadline } from '@/components/ui/columns-tasks';
import type { BulkDeadlineChanges } from '@/lib/deadlines/bulk-edit';
import type { DeadlineTagChanges } from '@/lib/deadlines/tags';

/** Success toast with an inline Undo action (used for reversible soft deletes). */
function showUndoToast(message: string, onUndo: () => void) {
  toast.success(
    (t) =>
      React.createElement(
        'span',
        { className: 'flex items-center gap-3' },
        message,
        React.createElement(
          'button',
          {
            className: 'font-semibold underline',
            onClick: () => {
              toast.dismiss(t.id);
              onUndo();
            },
          },
          'Undo',
        ),
      ),
  );
}

/** Format a Date as midnight UTC on the user's local date (YYYY-MM-DDT00:00:00.000Z).
 *  Avoids .toISOString() which shifts the date for timezones east of UTC. */
function toUTCDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}T00:00:00.000Z`;
}

const actionErrorMessage = (error: unknown, fallback: string) => apiErrorMessage(error) || fallback;

/** Fields the deadline update (PUT) endpoint accepts. All optional — only set keys are sent. */
interface DeadlineUpdatePayload {
  description?: string;
  notes?: string;
  date?: string;
  state?: string;
  assigned_to_guid?: string | null;
  assigned_to_name?: string | null;
  assigned_to_email?: string | null;
  party?: string | null;
}

interface UseDeadlineActionsOptions {
  slug: string;
  mutate: () => void;
  onSuccess?: () => void;
}

export function useDeadlineActions({ slug, mutate, onSuccess }: UseDeadlineActionsOptions) {
  const createDeadline = useCallback(async (newDeadline: Omit<Deadline, 'id' | 'created_by' | 'state'>) => {
    try {
      const formattedDate = newDeadline.date instanceof Date
        ? toUTCDateString(newDeadline.date)
        : toUTCDateString(new Date(newDeadline.date));

      const payload = {
        description: newDeadline.description,
        notes: newDeadline.notes || '',
        date: formattedDate,
        state: 'Confirmed',
        assigned_to_guid: newDeadline.assigned_to_guid,
        party: newDeadline.party || null,
      };

      await backendClient.post(`/deadlines/${slug}`, payload);
      toast.success('Deadline created successfully');

      await mutate();
      onSuccess?.();
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to create deadline'));
      throw error;
    }
  }, [slug, mutate, onSuccess]);

  const updateDeadline = useCallback(async (deadlineId: string, updates: Partial<Deadline>) => {
    try {
      const payload: DeadlineUpdatePayload = {};

      if (updates.description !== undefined) payload.description = updates.description;
      if (updates.notes !== undefined) payload.notes = updates.notes;
      if (updates.date !== undefined) {
        payload.date = updates.date instanceof Date
          ? toUTCDateString(updates.date)
          : toUTCDateString(new Date(updates.date));
      }
      if (updates.state !== undefined) payload.state = updates.state;
      if (updates.assigned_to_guid !== undefined) {
        payload.assigned_to_guid = updates.assigned_to_guid || null;
      }
      if (updates.assigned_to_name !== undefined) {
        payload.assigned_to_name = updates.assigned_to_name || null;
      }
      if (updates.assigned_to_email !== undefined) {
        payload.assigned_to_email = updates.assigned_to_email || null;
      }
      if (updates.party !== undefined) {
        payload.party = updates.party || null;
      }

      await backendClient.put(`/deadlines/${slug}/${deadlineId}`, payload);
      await mutate();
      toast.success('Deadline updated successfully');
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to update deadline'));
      throw error;
    }
  }, [slug, mutate]);

  const confirmDeadline = useCallback(async (deadlineId: string) => {
    try {
      await backendClient.put(`/deadlines/${slug}/${deadlineId}`, { state: 'Confirmed' });
      await mutate();
      toast.success('Deadline confirmed');
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to confirm deadline'));
      throw error;
    }
  }, [slug, mutate]);

  const completeDeadline = useCallback(async (deadlineId: string) => {
    try {
      await backendClient.put(`/deadlines/${slug}/${deadlineId}`, { state: 'Completed' });
      await mutate();
      toast.success('Deadline marked as complete');
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to complete deadline'));
      throw error;
    }
  }, [slug, mutate]);

  const restoreDeadline = useCallback(async (syntheticId: string) => {
    try {
      await backendClient.post(`/deadlines/${slug}/synthetic/${syntheticId}/restore`);
      await mutate();
      toast.success('Deadline restored');
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to restore deadline'));
      throw error;
    }
  }, [slug, mutate]);

  const bulkRestoreDeadlines = useCallback(async (syntheticIds: string[]) => {
    if (syntheticIds.length === 0) return;
    try {
      await backendClient.post(`/deadlines/${slug}/synthetic/bulk-restore`, { synthetic_ids: syntheticIds });
      await mutate();
      toast.success(`Restored ${syntheticIds.length} deadline${syntheticIds.length !== 1 ? 's' : ''}`);
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to restore deadlines'));
      throw error;
    }
  }, [slug, mutate]);

  const deleteDeadline = useCallback(async (deadline: Deadline | string, skipConfirm = false) => {
    const syntheticId = typeof deadline === 'string' ? null : (deadline.synthetic_deadline_id ?? null);
    const deadlineId = typeof deadline === 'string' ? deadline : deadline.id;

    if (!skipConfirm && !confirm('Are you sure you want to delete this deadline?')) {
      return;
    }

    try {
      if (syntheticId) {
        await backendClient.post(`/deadlines/${slug}/synthetic/${syntheticId}/delete`);
        await mutate();
        showUndoToast('Deadline deleted', () => { void restoreDeadline(syntheticId); });
      } else {
        await backendClient.delete(`/deadlines/${slug}/${deadlineId}`);
        await mutate();
        toast.success('Deadline deleted successfully');
      }
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to delete deadline'));
      throw error;
    }
  }, [slug, mutate, restoreDeadline]);

  const moveToInProgress = useCallback(async (deadlineId: string) => {
    try {
      await backendClient.put(`/deadlines/${slug}/${deadlineId}`, { state: 'Confirmed' });
      await mutate();
      toast.success('Moved to Confirmed');
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to update deadline'));
      throw error;
    }
  }, [slug, mutate]);

  const moveToNeedsConfirmation = useCallback(async (deadlineId: string) => {
    try {
      await backendClient.put(`/deadlines/${slug}/${deadlineId}`, { state: 'Extracted' });
      await mutate();
      toast.success('Moved to Needs Confirmation');
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to update deadline'));
      throw error;
    }
  }, [slug, mutate]);

  const updateAssignee = useCallback(async (deadlineId: string, assigneeGuid: string | null) => {
    try {
      await backendClient.put(`/deadlines/${slug}/${deadlineId}`, {
        assigned_to_guid: assigneeGuid
      });
      await mutate();
      toast.success(assigneeGuid ? 'Assignee updated' : 'Deadline unassigned');
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to update assignee'));
      throw error;
    }
  }, [slug, mutate]);

  const updateDate = useCallback(async (deadlineId: string, date: Date) => {
    try {
      await backendClient.put(`/deadlines/${slug}/${deadlineId}`, {
        date: toUTCDateString(date)
      });
      await mutate();
      toast.success('Due date updated');
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to update due date'));
      throw error;
    }
  }, [slug, mutate]);

  const updateParty = useCallback(async (deadlineId: string, party: string) => {
    try {
      await backendClient.put(`/deadlines/${slug}/${deadlineId}`, {
        party: party
      });
      await mutate();
      toast.success('Party updated successfully');
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to update party'));
      throw error;
    }
  }, [slug, mutate]);

  const bulkDeleteDeadlines = useCallback(async (deadlines: Deadline[]) => {
    if (deadlines.length === 0) return;

    const syntheticIds = deadlines
      .map((t) => t.synthetic_deadline_id)
      .filter((id): id is string => !!id);
    const otherIds = deadlines.filter((t) => !t.synthetic_deadline_id).map((t) => t.id);

    try {
      if (syntheticIds.length > 0) {
        await backendClient.post(`/deadlines/${slug}/synthetic/bulk-delete`, { synthetic_ids: syntheticIds });
      }
      if (otherIds.length > 0) {
        await backendClient.post(`/deadlines/${slug}/bulk-delete`, { deadline_ids: otherIds });
      }
      await mutate();

      const total = syntheticIds.length + otherIds.length;
      const message = `Deleted ${total} deadline${total !== 1 ? 's' : ''}`;
      if (syntheticIds.length > 0) {
        showUndoToast(message, () => { void bulkRestoreDeadlines(syntheticIds); });
      } else {
        toast.success(message);
      }
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to delete deadlines'));
      throw error;
    }
  }, [slug, mutate, bulkRestoreDeadlines]);

  const addTag = useCallback(async (syntheticId: string, label: string) => {
    try {
      await backendClient.post(`/deadlines/${slug}/synthetic/${syntheticId}/tags`, { label });
      await mutate();
      toast.success('Tag added');
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to add tag'));
      throw error;
    }
  }, [slug, mutate]);

  const removeTag = useCallback(async (syntheticId: string, tagId: string) => {
    try {
      await backendClient.delete(`/deadlines/${slug}/synthetic/${syntheticId}/tags/${tagId}`);
      await mutate();
      toast.success('Tag removed');
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to remove tag'));
      throw error;
    }
  }, [slug, mutate]);

  /** Apply a batch of staged tag edits (edit dialog). Success is silent: the
   *  surrounding save already confirms, so per-tag toasts would just be noise. */
  const saveTags = useCallback(async (syntheticId: string, changes: DeadlineTagChanges) => {
    try {
      for (const tagId of changes.remove) {
        try {
          await backendClient.delete(`/deadlines/${slug}/synthetic/${syntheticId}/tags/${tagId}`);
        } catch (error) {
          if (apiErrorStatus(error) !== 404) throw error;
        }
      }
      for (const label of changes.add) {
        await backendClient.post(`/deadlines/${slug}/synthetic/${syntheticId}/tags`, { label });
      }
      await mutate();
    } catch (error) {
      await mutate();
      toast.error(actionErrorMessage(error, 'Failed to update tags'));
      throw error;
    }
  }, [slug, mutate]);

  const bulkUpdateDeadlines = useCallback(async (changes: BulkDeadlineChanges) => {
    const shouldUpdateState = changes.state !== undefined && changes.stateTaskGuids.length > 0;
    const shouldUpdateAssignee = changes.assignedToGuid !== undefined && changes.assigneeTaskGuids.length > 0;

    if (!shouldUpdateState && !shouldUpdateAssignee) return;

    try {
      const payload: {
        state?: string;
        state_deadline_ids: string[];
        assigned_to_guid?: string | null;
        assignee_deadline_ids: string[];
      } = {
        state_deadline_ids: shouldUpdateState ? changes.stateTaskGuids : [],
        assignee_deadline_ids: shouldUpdateAssignee ? changes.assigneeTaskGuids : [],
      };

      if (shouldUpdateState) {
        payload.state = changes.state;
      }
      if (shouldUpdateAssignee) {
        payload.assigned_to_guid = changes.assignedToGuid;
      }

      const response = await backendClient.post(`/deadlines/${slug}/bulk-update`, payload);
      await mutate();

      const stateCount = response.data?.state_count ?? payload.state_deadline_ids.length;
      const assigneeCount = response.data?.assignee_count ?? payload.assignee_deadline_ids.length;
      const changedCount = new Set([
        ...(response.data?.updated_state_guids ?? payload.state_deadline_ids),
        ...(response.data?.updated_assignee_guids ?? payload.assignee_deadline_ids),
      ]).size;

      const stateMessage = stateCount > 0
        ? `${stateCount} status change${stateCount !== 1 ? 's' : ''}`
        : null;
      const assigneeMessage = assigneeCount > 0
        ? `${assigneeCount} assignee change${assigneeCount !== 1 ? 's' : ''}`
        : null;

      toast.success(
        `Updated ${changedCount} deadline${changedCount !== 1 ? 's' : ''}` +
        (stateMessage || assigneeMessage
          ? ` (${[stateMessage, assigneeMessage].filter(Boolean).join(', ')})`
          : '')
      );
    } catch (error) {
      toast.error(actionErrorMessage(error, 'Failed to update deadlines'));
      throw error;
    }
  }, [slug, mutate]);

  return useMemo(
    () => ({
      createDeadline,
      updateDeadline,
      confirmDeadline,
      completeDeadline,
      deleteDeadline,
      moveToInProgress,
      moveToNeedsConfirmation,
      updateAssignee,
      updateDate,
      updateParty,
      bulkDeleteDeadlines,
      bulkUpdateDeadlines,
      restoreDeadline,
      bulkRestoreDeadlines,
      addTag,
      removeTag,
      saveTags,
    }),
    [
      createDeadline,
      updateDeadline,
      confirmDeadline,
      completeDeadline,
      deleteDeadline,
      moveToInProgress,
      moveToNeedsConfirmation,
      updateAssignee,
      updateDate,
      updateParty,
      bulkDeleteDeadlines,
      bulkUpdateDeadlines,
      restoreDeadline,
      bulkRestoreDeadlines,
      addTag,
      removeTag,
      saveTags,
    ],
  );
}
